import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { normalizeError } from "@/lib/normalizeError";
import { Loader2, ExternalLink, User, CreditCard, Rocket, Shield } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import { SEOHead } from "@/components/SEOHead";

const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tier, currentPeriodEnd, refetch } = useSubscription();

  const [vercelHookUrl, setVercelHookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  const currentPlan = tier.charAt(0).toUpperCase() + tier.slice(1);
  const nextBillingDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

  // Handle checkout success redirect (from Stripe via /account redirect)
  useEffect(() => {
    if (searchParams.get("checkout") === "success") {
      toast.success("Subscription activated! Welcome aboard.");
      refetch();
      // Clean up URL
      navigate("/settings", { replace: true });
    }
  }, [searchParams, refetch, navigate]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!user && !loading) {
      navigate("/signin");
    }
  }, [user, loading, navigate]);

  // Load user's Vercel hook URL
  useEffect(() => {
    const loadSettings = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('vercel_deploy_hook')
          .eq('id', user.id)
          .single();

        if (!error && data?.vercel_deploy_hook) {
          setVercelHookUrl(data.vercel_deploy_hook);
        }
      } catch (err) {
        const e = normalizeError(err);
        console.error('Error loading settings:', e.name, e.message, e.raw);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user]);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session");
      if (error) {
        throw new Error(error.message || "Failed to open billing portal");
      }
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No portal URL returned");
      }
    } catch (err) {
      console.error("[Settings] Portal error:", err);
      toast.error("Could not open billing portal. Try again.");
      setPortalLoading(false);
    }
  };

  const handleSaveVercelHook = async () => {
    if (!user) {
      toast.error('Please sign in to save settings');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ vercel_deploy_hook: vercelHookUrl || null })
        .eq('id', user.id);

      if (error) throw error;
      toast.success('Vercel Deploy Hook URL saved');
    } catch (err) {
      const e = normalizeError(err);
      console.error('Error saving settings:', e.name, e.message, e.raw);
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Settings - DayOne"
        description="Manage your DayOne account, subscription, and preferences."
      />

      <div className="container py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
            <p className="text-muted-foreground mt-1">
              Manage your account and preferences
            </p>
          </div>

          <div className="space-y-6">
            {/* Account Information */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  <CardTitle>Account</CardTitle>
                </div>
                <CardDescription>Your account information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Email</Label>
                  <p className="font-medium">{user.email}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">User ID</Label>
                  <p className="font-mono text-sm text-muted-foreground">{user.id}</p>
                </div>
              </CardContent>
            </Card>

            {/* Subscription */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  <CardTitle>Subscription</CardTitle>
                </div>
                <CardDescription>Manage your billing and subscription</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-muted-foreground">Current Plan</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="font-semibold text-lg">{currentPlan}</p>
                      {tier === "free" && (
                        <Badge variant="secondary">Free Tier</Badge>
                      )}
                      {tier !== "free" && (
                        <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-400">Active</Badge>
                      )}
                    </div>
                  </div>
                  <Button onClick={() => navigate("/pricing")}>
                    {tier === "free" ? "Upgrade Plan" : "Change Plan"}
                  </Button>
                </div>

                {nextBillingDate && (
                  <div>
                    <Label className="text-sm text-muted-foreground">Next Billing Date</Label>
                    <p className="font-medium">{nextBillingDate}</p>
                  </div>
                )}

                {tier === "free" && (
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-sm">
                      You're on the free plan. Upgrade to Starter for $29/month to unlock more projects, AI edits, custom domains, and more.
                    </p>
                  </div>
                )}
              </CardContent>
              {tier !== "free" && (
                <CardFooter className="flex gap-3">
                  <Button variant="outline" onClick={handleOpenPortal} disabled={portalLoading}>
                    {portalLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Manage Subscription
                  </Button>
                </CardFooter>
              )}
            </Card>

            {/* Deployment */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Rocket className="h-5 w-5" />
                  <CardTitle>Deployment</CardTitle>
                </div>
                <CardDescription>Configure your deployment settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="vercel-hook">Vercel Deploy Hook URL</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Add your Vercel Deploy Hook URL to enable one-click deployment from Code Workspace.
                    You can find this in your Vercel project settings under Git → Deploy Hooks.
                  </p>
                  {loading ? (
                    <div className="flex items-center gap-2 py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">Loading...</span>
                    </div>
                  ) : (
                    <>
                      <Input
                        id="vercel-hook"
                        type="url"
                        placeholder="https://api.vercel.com/v1/integrations/deploy/..."
                        value={vercelHookUrl}
                        onChange={(e) => setVercelHookUrl(e.target.value)}
                      />
                      <div className="flex items-center gap-2 mt-3">
                        <Button onClick={handleSaveVercelHook} disabled={saving}>
                          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                          Save Deploy Hook
                        </Button>
                        <a
                          href="https://vercel.com/docs/deployments/deploy-hooks"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline flex items-center gap-1"
                        >
                          Learn more <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Privacy & Security */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  <CardTitle>Privacy & Security</CardTitle>
                </div>
                <CardDescription>Manage your data and account</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium">Data Export</p>
                    <p className="text-sm text-muted-foreground">Download your saved ideas and projects</p>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <a href="mailto:support@usedayone.net?subject=Data%20Export%20Request">Request</a>
                  </Button>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium">Delete Account</p>
                    <p className="text-sm text-muted-foreground">Permanently remove your data</p>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <a href="mailto:support@usedayone.net?subject=Delete%20Account%20Request">Request Deletion</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
